package com.advaita.cloud.drive.ui.api

/**
 * A non-2xx response from the drive API, carrying the status so callers can render language
 * instead of a code.
 *
 * ADV-443: failures used to surface verbatim — "Delete failed: delete file failed: HTTP 503 " —
 * duplicated "failed", raw status, and a dangling space because `statusText` is empty on these
 * responses. This ticket makes 503 a routine outcome (az2 unreachable and drive correctly
 * declining to judge the token), so the status→language mapping lives here once and every
 * caller reads [userMessage]. The exception's own message stays technical for the console.
 */
class ApiException(
    val status: Int,
    val action: String,
    val statusText: String = "",
    /**
     * ADV-1326c (ruling 6): the body's typed `error`, read only where a caller acts on it (a
     * creation's `403`). Null everywhere else.
     */
    val code: String? = null,
    /**
     * ADV-1330 (the shared-folder create slice): the sentence for this refusal, when the body
     * says more than its status. It replaces the status's sentence in [userMessage]. Example:
     * a curated run whose output folder refuses `upload` says so, not "You don't have permission
     * to do that."
     */
    val detail: String? = null,
    /**
     * ADV-1330 (fresh-eyes L-2): true on a `404` that says a create's destination, or a curated
     * run's pick, is gone for the caller (a shared grant removed). A `404` from an upload
     * session's part, confirm or complete (`upload session not found`) is not one.
     */
    val gone: Boolean = false,
) : RuntimeException("$action failed: HTTP $status $statusText".trim()) {

    /** What the user is shown. Never contains a status code unless we have nothing better. */
    val userMessage: String = detail ?: when {
        // The most common case now. Deliberately does NOT name a subsystem: the auth filter is
        // the main emitter, but the ingress also 503s during a rollout, and naming the wrong
        // one is worse than saying nothing. The console line identifies it.
        status == SERVICE_UNAVAILABLE -> "Temporarily unavailable. Please try again shortly."
        status == UNAUTHORIZED -> "Your session has expired — please sign in again."
        status == FORBIDDEN -> "You don't have permission to do that."
        status >= SERVER_ERROR -> "Something went wrong on our end. Please try again shortly."
        else -> "That didn't work ($status)."
    }

    private companion object {
        const val UNAUTHORIZED = 401
        const val FORBIDDEN = 403
        const val SERVER_ERROR = 500
        const val SERVICE_UNAVAILABLE = 503
    }
}

/**
 * Thrown by [AuthApi] purely as control flow when it is navigating away — never a real error.
 * Callers up the stack catch `Throwable` broadly, so this needs to be recognisable: without it
 * the user was shown "Couldn't load this folder. login loop detected — not redirecting". ADV-443.
 */
class RedirectingException(reason: String) : RuntimeException(reason)

/**
 * The user-facing text for any throwable.
 *
 * Walks the cause chain, because failures get wrapped on the way up — `UploadOrchestrator` rethrows
 * as `RuntimeException("$label failed after N attempts", lastErr)`, so matching on `this` alone
 * meant uploads never got the mapped copy. Cycle-safe via [causeChain]. ADV-443.
 */
fun Throwable.userFacingMessage(): String {
    causeChain().forEach { t ->
        when (t) {
            is ApiException -> return t.userMessage
            is RedirectingException -> return "Signing you in…"
            else -> Unit
        }
    }
    return message ?: "Something went wrong. Please try again."
}

/** ADV-1326c (ruling 6): the refusal of a creation that the web answers with one more admission poll. */
const val CAPABILITY_DENIED = "capability_denied"

/**
 * True when this failure, or a cause of it, is `403 capability_denied`. Only a creation carries
 * the code ([ApiException.code]), so every other 403 answers false.
 */
fun Throwable.isCapabilityDenied(): Boolean =
    causeChain().any { it is ApiException && it.status == 403 && it.code == CAPABILITY_DENIED }

/**
 * ADV-1330 (the shared-folder create slice, scope 5): true when this failure, or a cause of it,
 * is a `403`, or a `404` that says the destination or a pick is gone ([ApiException.gone]).
 * Inside a shared folder either means the grant changed: a lowered level answers `403`, and a
 * removed grant answers `404` (the caller no longer reads the folder). The web then reads the
 * grants again, whatever the code.
 */
fun Throwable.isRefusal(): Boolean =
    causeChain().any { it is ApiException && (it.status == 403 || (it.status == 404 && it.gone)) }

/**
 * ADV-1330 (review): true when this failure, or a cause of it, is a [ApiException.gone] `404`
 * with no sentence of its own ([ApiException.detail]). A create in a shared folder then says the
 * folder is no longer shared, not "That didn't work (404)."
 */
fun Throwable.isDestinationGone(): Boolean =
    causeChain().any { it is ApiException && it.status == 404 && it.gone && it.detail == null }

/** `this` plus its causes, stopping on a self-reference or a cycle so a bad chain can't hang. */
private fun Throwable.causeChain(): List<Throwable> {
    val seen = mutableListOf<Throwable>()
    var current: Throwable? = this
    while (current != null && seen.none { it === current } && seen.size < MAX_CAUSE_DEPTH) {
        seen.add(current)
        current = current.cause
    }
    return seen
}

private const val MAX_CAUSE_DEPTH = 16
